import { useTheme } from 'bold-ui'
import { green, red } from 'bold-ui/lib/styles/colors'
import { Chart, ChartBody, ChartContainer, ChartFooter, DateRange, ReferenceArea, ValueRange } from 'components/chart'
import { SexoEnum } from 'graphql/types.generated'
import moment, { Duration, Moment } from 'moment'
import React from 'react'
import { reduceMedicoesIguais } from 'util/atendimento/reduceMedicoesIguais'
import { parseNumber } from 'util/number'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/HistoricoMedicaoModel'

import { LinhaTooltipGrafico } from '../../../LinhaTooltipGrafico'
import { getReferenceAreasCalculators, ReferenceAreasCalculator } from './referenceAreaCalculators'
import { TickIdade } from './TickIdade'

export interface GraficoPerimetroCefalicoProps {
  medicoes: HistoricoMedicaoModel[]
  dataNascimento: Moment
  dataRange: DateRange
  sexo: SexoEnum
}

export function GraficoPerimetroCefalicoView(props: GraficoPerimetroCefalicoProps) {
  const { medicoes, dataNascimento, dataRange, sexo } = props
  const theme = useTheme()

  const idadeNoRange = moment.duration(dataRange.end.diff(dataNascimento))
  const medicoesComPerimetroCefalico = reduceMedicoesIguais(medicoes, 'valorPerimetroCefalico') ?? []
  const seriesData = medicoesComPerimetroCefalico.map((m) => ({
    x: m.dataMedicao,
    y: parseNumber(m.valorPerimetroCefalico.replace('.', ',')),
  }))
  const yRange = { init: 30, end: 53, step: 1 }

  return (
    <ChartContainer>
      <ChartBody height={500}>
        <Chart<Moment>
          series={[{ name: 'Perímetro cefálico', data: seriesData, color: theme.pallete.gray.c20 }]}
          referenceAreas={getReferenceAreas(sexo, dataRange, yRange, idadeNoRange, dataNascimento)}
          xAxis={{
            title: 'Idade (meses completos e anos)',
            domain: dataRange,
            tickRenderer: (props) => <TickIdade {...props} dataNascimento={dataNascimento} />,
          }}
          yAxis={{ title: 'Perímetro cefálico', unit: 'cm', domain: yRange }}
          showLegend={false}
          tooltip={{
            type: 'point',
            render: (points) => (
              <>
                {points?.map((p) => (
                  <LinhaTooltipGrafico key={p.y}>{`Perímetro cefálico: ${p.y} cm`}</LinhaTooltipGrafico>
                ))}
                <LinhaTooltipGrafico>{`Data: ${moment(points[0].x).format('DD/MM/YYYY')}`}</LinhaTooltipGrafico>
              </>
            ),
          }}
        />
      </ChartBody>
      <ChartFooter>{getFooterText(sexo, idadeNoRange)}</ChartFooter>
    </ChartContainer>
  )
}

function getFooterText(sexo: SexoEnum, idadeNoRange: Duration): string {
  if (idadeNoRange.years() <= 2) {
    if (sexo === SexoEnum.MASCULINO) return 'Fonte: Caderneta de Saúde da Criança Menino - 2020.'
    else if (sexo === SexoEnum.FEMININO) return 'Fonte: Caderneta de Saúde da Criança Menina - 2020.'
  }
  return ''
}

function getReferenceAreas(
  sexo: SexoEnum,
  xRange: DateRange,
  yRange: ValueRange,
  idadeNoRange: Duration,
  dataNascimento: Moment
): ReferenceArea<Moment>[] {
  if (idadeNoRange.years() <= 2) {
    const refAreaCalculators = getReferenceAreasCalculators(sexo, dataNascimento, xRange, 'perimetroCefalico')
    return [
      getReferenceAreaAbaixoEsperado(refAreaCalculators),
      getReferenceAreaAdequado(refAreaCalculators),
      getReferenceAreaAcimaEsperado(refAreaCalculators),
    ]
  } else return undefined
}

function getReferenceAreaAbaixoEsperado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-2),
    })),
    name: 'Abaixo do esperado',
    color: red.c90,
    strokeColor: red.c60,
    tickColor: red.c60,
  }
}

function getReferenceAreaAdequado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(2),
    })),
    name: 'Adequado',
    color: green.c90,
    strokeColor: red.c60,
    tickColor: green.c60,
  }
}

function getReferenceAreaAcimaEsperado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
    })),
    name: 'Acima do esperado',
    color: red.c90,
    stroke: false,
    tickColor: red.c60,
  }
}
